import { UserAttributeTypeIDCustom, UserAttributeTypeIDDirectoryStatus } from 'lib/constants/userattributes'

export type validConditionTypes = 'hasEntitlement' | 'profileEquals'

export type hasEntitlementCondition = {
    id: string
    type: 'hasEntitlement'
    appId?: string
    appEntitlementId?: string
}

export type profileEqualsCondition = {
    id: string
    type: 'profileEquals'
    field?: string
    value?: string
    attributeTypeId?: string
}

export type Condition = (hasEntitlementCondition | profileEqualsCondition) & { type: validConditionTypes }

export type Operator = {
    id: string
    type: 'and' | 'or'
}

export type Expression = Condition | Operator

export const isOperator = (expression: Expression): boolean => expression.type === 'and' || expression.type === 'or'

export const toSnakeCase = (str: string): string => {
    return str
        .trim()
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .replace(/^_/, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
}

const conditions: Array<{ regex: RegExp, type: validConditionTypes, isStandardAttribute?: boolean, field?: string }> = [
    {
        regex: /^c1\.user\.v1\.HasEntitlement\(subject, "([a-zA-Z0-9]{27})", "([a-zA-Z0-9]{27})"\)/,
        type: 'hasEntitlement',
    },
    {
        // Match directory_status enum comparison: subject.directory_status == UserStatus.(ENUM)
        regex: /^subject\.directory_status\s*==\s*UserStatus\.(\w+)/,
        type: 'profileEquals',
        field: 'directory_status',
    },
    {
        regex: /^has\(subject\.profile\.([^ ]+)\) && subject\.profile\.([^ ]+) == "([^"]+)"/,
        type: 'profileEquals',
    },
    {
        // Match standard attributes with bracket notation: subject.profile["key"] == "value"
        // Handles flexible whitespace - makes spaces around 'in' optional
        // The opening/closing parens in the presence check are optional to handle cases where
        // we're already inside nested parentheses and parentheses were already consumed
        regex: /^(?:(?:\()?"([^"]+)"\s*in\s*subject\.profile\)?\s*&&\s*)?subject\.profile\["([^"]+)"\]\s*==\s*"([^"]+)"/,
        type: 'profileEquals',
    },
    { // Match standard attributes with dot notation: has(subject.jobTitle) && subject.jobTitle == "value"
        regex: /^has\(subject\.([^ ]+)\) && subject\.([^ ]+) == "([^"]+)"/,
        type: 'profileEquals',
        isStandardAttribute: true,
    },
]

// TODO(**): stop using exceptions for flow control
export const parseExpression = (expression: string): Array<Expression | Expression[]> => {
    const parsedExpressions: Array<Expression | Expression[]> = []
    let index = 0
    let nestedExpression: Array<Expression> = []
    let isNestedExpression = false
    const stackOfParens: string[] = []

    if (expression === 'false' || !expression) {
        return []
    }

    while (index < expression.length) {
        const charAtIndex = expression[index]

        if (charAtIndex === '(') {
            // parse subexpression
            // We shouldn't allow more than 1 level of nested parens.
            if (isNestedExpression && stackOfParens.length >= 2) {
                throw new Error('nested expression inside nested expression')
            }
            isNestedExpression = true
            stackOfParens.push(charAtIndex)
            index += 1
        } else if (charAtIndex === ')') {
            // end subexpression
            if (!stackOfParens.length) {
                throw new Error('no matching open paren')
            }
            stackOfParens.pop()
            if (stackOfParens.length === 0) {
                parsedExpressions.push(nestedExpression)
                // reset nested expression
                nestedExpression = []
                isNestedExpression = false
            }
            index += 1
        } else if (charAtIndex === ' ') {
            // skip whitespace
            index += 1
        } else if (charAtIndex === '|') {
            // parse OR operator
            const exp: Operator = { id: crypto.randomUUID(), type: 'or' }
            if (isNestedExpression) {
                nestedExpression.push(exp)
            } else {
                parsedExpressions.push(exp)
            }
            index += 2
        } else if (charAtIndex === '&') {
            // parse AND operator
            const exp: Operator = { id: crypto.randomUUID(), type: 'and' }
            if (isNestedExpression) {
                nestedExpression.push(exp)
            } else {
                parsedExpressions.push(exp)
            }
            index += 2
        } else {
            // parse condition
            let conditionMatched = false
            for (let i = 0; i < conditions.length; i++) {
                const condition = conditions[i]
                const match = expression.slice(index).match(condition.regex)
                if (match) {
                    let expToPush: Condition | undefined
                    switch (condition.type) {
                        case 'hasEntitlement':
                            expToPush = {
                                id: crypto.randomUUID(),
                                type: condition.type,
                                appId: match[1],
                                appEntitlementId: match[2],
                            }
                            break
                        case 'profileEquals': {
                            // Determine field name: use explicit field from condition, or extract from regex capture
                            const fieldName = condition.field ?? match[2]

                            // For patterns with explicit field (e.g., directory_status), value is in first capture group
                            // For other patterns, value is in third capture group
                            const fieldValue = condition.field ? match[1] : match[3]

                            // For patterns with explicit field, don't set attributeTypeId - UI will resolve it via API lookup
                            // For other patterns: isStandardAttribute true = standard (''), false = custom
                            const attributeTypeId = condition.field ||
                            condition.isStandardAttribute ? '' : UserAttributeTypeIDCustom

                            expToPush = {
                                id: crypto.randomUUID(),
                                type: condition.type,
                                field: fieldName,
                                value: fieldValue,
                                attributeTypeId,
                            }
                            break
                        }
                        default:
                            throw new Error('unknown condition type')
                    }

                    if (!expToPush) {
                        throw new Error('no condition to push')
                    }

                    if (isNestedExpression) {
                        nestedExpression.push(expToPush)
                    } else {
                        parsedExpressions.push([expToPush])
                    }

                    index += match[0].length
                    conditionMatched = true
                    break
                }
            }

            // if no condition matched, throw error
            if (!conditionMatched) {
                throw new Error('no condition matched')
            }
        }
    }

    return parsedExpressions
}

export const convertExpressionToCelString = (expressions: Array<Expression | Expression[]>): string => {
    if (expressions.length === 0) {
        return 'false'
    }

    return expressions.map((exp) => {
        if (Array.isArray(exp)) {
            return `(${convertExpressionToCelString(exp)})`
        }
        switch (exp.type) {
            case 'hasEntitlement':
                return `(c1.user.v1.HasEntitlement(subject, "${exp.appId}", "${exp.appEntitlementId}"))`
            case 'profileEquals': {
                const field = exp.field?.trim()
                if (!field) {
                    return 'false'
                }

                // Handle directory_status specially - use enum comparison
                // Check attributeTypeId, or field name (both snake_case from parser and display name from UI)
                const isDirectoryStatus = exp.attributeTypeId === UserAttributeTypeIDDirectoryStatus
                    || field === 'directory_status'
                    || toSnakeCase(field) === 'directory_status'
                if (isDirectoryStatus) {
                    // Directory status uses UserStatus enum values (ENABLED, DISABLED, DELETED, UNKNOWN)
                    return `(subject.directory_status == UserStatus.${exp.value})`
                }

                // Convert standard field to snake_case with no trailing spaces
                if (exp.attributeTypeId != null && exp.attributeTypeId !== UserAttributeTypeIDCustom) {
                    const snakeCaseField = toSnakeCase(field)
                    return `(has(subject.${snakeCaseField}) && subject.${snakeCaseField} == "${exp.value}")`
                }

                // Custom attribute: use subject.profile["<field>"] or subject.profile.<field>
                // Use bracket notation if field contains spaces or special characters
                // Valid identifier characters: letters, numbers, underscore, dollar sign
                const hasSpecialChars = /[^a-zA-Z0-9_$]/.test(field)
                if (hasSpecialChars) {
                    // Use bracket notation and check for existence to avoid "no such key" errors
                    return `("${field}" in subject.profile && subject.profile["${field}"] == "${exp.value}")`
                }

                // Use dot notation with has() check for custom attributes
                // if exp.attributeTypeId is not set, it means it's a pre-existing field attribute (case added for backwards compatibility)
                return `(has(subject.profile.${field}) && subject.profile.${field} == "${exp.value}")`
            }
            case 'and':
                return '&&'
            case 'or':
                return '||'
            default:
                return 'unknown'
        }
    }).join(' ')
}
